import { Dispatch, SetStateAction } from 'react'
import { AnyObject, Form } from 'react-final-form'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird'

import Notification from '@infura/packages.components/Notification'
import { Button } from '@infura/packages.ui/Button'
import { Card } from '@infura/packages.ui/Card'

import FieldInput from 'components/FieldInput'

import { Infura } from 'lib/types/infuraTypes'

interface ResetMFAProps {
  onSubmit: ({ code }) => Promise<AnyObject>
  setLoginStep: Dispatch<SetStateAction<Infura.AUTH_LOGIN_STEP>>
}

export default function ResetMFA({ onSubmit, setLoginStep }: Readonly<ResetMFAProps>) {
  return (
    <section className='flex h-full w-full items-center justify-center'>
      <div className='w-full max-w-[37rem] px-6'>
        <Card className='w-full bg-transparent pb-6 shadow-none'>
          <h1 className='font-body mb-10 text-4xl font-bold'>Verify with Backup Code</h1>
          <p className='mb-8 text-lg'>Signing in with your backup code will reset your two-factor authentication.</p>
          <Form
            onSubmit={onSubmit}
            render={({ handleSubmit, submitError, submitting }) => (
              <>
                {submitError ? (
                  <Notification
                    className='mb-6'
                    color='alert'
                    data-testid='auth-form-error'
                    size='sm'
                    variant='outlined'
                  >
                    <span className='w-full text-center'>{submitError}</span>
                  </Notification>
                ) : null}
                <form className='text-lg' data-testid='auth-form' onSubmit={handleSubmit}>
                  <div>
                    <FieldInput
                      autoComplete='off'
                      className='mb-7 !h-auto !px-6 !py-5 text-lg 2xl:text-xl'
                      data-testid='backup_code'
                      id='code'
                      label={<span className='mb-2'>Backup Code</span>}
                      name='code'
                      placeholder='XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX'
                      required
                      type='text'
                    />

                    <div className='mb-10 flex flex-col items-center justify-between gap-6 2xl:flex-row 2xl:gap-0'>
                      <Button
                        className='2xl:!w-auto'
                        data-testid='auth-button'
                        disabled={submitting}
                        fullWidth
                        type='submit'
                      >
                        {submitting ? (
                          <FontAwesomeIcon className='fa-fw fa-spin text-lg' icon={faSpinnerThird} />
                        ) : (
                          'Verify'
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              </>
            )}
          />
          <div className='border-t border-gray-400 pb-8 pt-10 text-center lg:pb-16 lg:text-left'>
            <p className='mb-0 text-lg text-white lg:mr-4 2xl:text-xl'>Don’t have access to your backup code?</p>
            <a
              className='text-lg underline 2xl:text-xl'
              onClick={() => setLoginStep(Infura.AUTH_LOGIN_STEP.MFA_RECOVER)}
            >
              <span>Try recovering your account.&nbsp;</span>
              <FontAwesomeIcon className='ml-1' icon={faArrowRight} />
            </a>
          </div>
        </Card>
      </div>
    </section>
  )
}
